import React, {
  ReactNode,
  createContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { GraphConfiguration } from "@/type/common";
import { getDefaultConfig } from "@/helpers/default";
import { refactor_fields } from "@/utils/refactor";
import axios from "@/services/axios";

// Define the context type
type GraphConfigContextType = {
  config: GraphConfiguration | null;
  setConfig: (config: GraphConfiguration) => void;
  resetConfig: () => void;
  openConfigModal: (data: any) => void;
  fields: any[];
  step: string;
  setStep: (step: string) => void;
  modelLoaded: string | null;
  loadFields: (model: string) => Promise<void>;
  showAi: boolean;
  setShowAi: (show: boolean) => void;
};

// Create the initial context value
const initialGraphConfigContext: GraphConfigContextType = {
  config: null,
  setConfig: () => null,
  resetConfig: () => null,
  openConfigModal: () => null,
  fields: [],
  step: "config",
  setStep: () => null,
  modelLoaded: null,
  loadFields: async () => {},
  showAi: false,
  setShowAi: () => null,
};

// Create the context
const GraphConfigContext = createContext<GraphConfigContextType>(
  initialGraphConfigContext
);

// Create the provider component
type GraphConfigProviderProps = {
  children: ReactNode;
};

export const GraphConfigProvider: React.FC<GraphConfigProviderProps> = ({
  children,
}) => {
  const [config, setConfig] = useState<GraphConfiguration | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [step, setStep] = useState<string>("model");
  const modelLoadedRef = useRef<string | null>(null);
  const [showAi, setShowAi] = useState(false);

  const loadFields = async (model: string) => {
    if (modelLoadedRef.current === model) return;
    modelLoadedRef.current = model;

    const fieldsData = await axios
      .post("/odash/execute", {
        action: "get_model_fields",
        parameters: { model_name: model },
      })
      .then((e) => e.data)
      .catch((_e) => null);

    if (fieldsData) {
      const refactoredFields = refactor_fields(fieldsData) as any;
      setFields(refactoredFields);
    }
  };

  const openConfigModal = async (data: any) => {
    const existingData = await axios
      .get(`/odash/data/${data.id}`)
      .then((e) => e.data)
      .catch((_e) => null);
    if (existingData) {
      setConfig(existingData);
      if (existingData.model !== "") {
        loadFields(existingData.model);
        setStep("config");
      }
    } else {
      const initialData = getDefaultConfig(data.type);
      setConfig({ ...initialData, id: data.id });
    }
  };

  const resetConfig = () => {
    setConfig(null);
    setStep("model");
  };

  useEffect(() => {
    if (config?.model && config?.model !== "") {
      loadFields(config.model);
    }
  }, [config?.model]);

  const contextValue: GraphConfigContextType = {
    config,
    setConfig,
    resetConfig,
    openConfigModal,
    fields,
    step,
    setStep,
    modelLoaded: modelLoadedRef.current,
    loadFields,
    showAi,
    setShowAi,
  };

  return (
    <GraphConfigContext.Provider value={contextValue}>
      {children}
    </GraphConfigContext.Provider>
  );
};

export const useGraphConfig = () => {
  const context = React.useContext(GraphConfigContext);
  if (context === undefined) {
    throw new Error("useGraphConfig must be used within a GraphConfigProvider");
  }
  return context;
};

export default GraphConfigContext;
